// One of my orders — GET customer/me/orders/{id}, ownership-scoped by the
// server (someone else's order is a 404, never a peek). The receipt as
// the kitchen priced it: lines, offers, fees, the total and what is paid,
// with the live tracking page one tap away.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { OrderLines } from '../../components/OrderLines';
import { CHANNEL_LABEL, fetchMyOrder, money, type Order } from '../../lib/api';
import { AccountHead } from './shared';

export function OrderDetail() {
  const { rid, sid, orderId } = useParams() as { rid: string; sid: string; orderId: string };
  const [o, setO] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMyOrder(rid, orderId).then(
      (v) => { setO(v); document.title = `Order ${v.docketLabel ?? ''}`; },
      (e) => setError(e instanceof Error ? e.message : 'Could not load the order.'),
    );
  }, [rid, orderId]);

  const placed = o ? new Date(o.placedAt) : null;
  const live = o && (o.status === 'new' || o.status === 'accepted' || o.status === 'in_progress');

  return (
    <div className="co-page">
      <AccountHead back={`/r/${rid}/s/${sid}/orders`} backLabel="Back to my orders" kicker="My orders" title={o?.docketLabel ? `Order ${o.docketLabel}` : 'Order'} />
      {error && <div className="co-err acct-err" role="alert">{error}</div>}
      {!o && !error && <div className="loading">Finding your order…</div>}
      {o && placed && (
        <>
          <section className="co-sec" data-section="order-detail" data-order={o.orderId} data-status={o.status} data-payment-state={o.paymentState}>
            <div className="acct-od-top">
              <span className="mo-status">{o.status.replace(/_/g, ' ')}</span>
              <span className="acct-od-when">{placed.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })} · {placed.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}</span>
            </div>
            <div className="acct-od-how">
              {CHANNEL_LABEL[o.collectionType]}
              {o.delivery && ` · ${o.delivery.addressLine}, ${o.delivery.suburb}`}
              {o.dineIn && ` · Table ${o.dineIn.tableName}`}
              {o.requestedFor && ` · for ${new Date(o.requestedFor).toLocaleString('en-AU', { weekday: 'short', hour: 'numeric', minute: '2-digit' })}`}
            </div>
            {o.statusReason && <div className="co-hint">{o.statusReason}</div>}
            {live && <Link to={`/r/${rid}/s/${sid}/track/${o.orderId}`} className="co-back">Track this order live →</Link>}
          </section>

          <section className="co-sec" data-section="lines">
            <h2>What you ordered</h2>
            <OrderLines lines={o.lines} />
            {o.comments && <div className="co-hint">Note for the kitchen: {o.comments}</div>}
            <div className="co-totals">
              <div className="tr"><span>Subtotal</span><span data-subtotal>{money(o.subtotalInCents)}</span></div>
              {o.discounts.map((d) => <div className="tr neg" key={d.rowId} data-discount={d.discountType}><span>{d.label}</span><span>− {money(d.amountInCents)}</span></div>)}
              {o.surcharges.map((d) => <div className="tr" key={d.rowId}><span>{d.label}</span><span>{money(d.amountInCents)}</span></div>)}
              {o.deliveryFeeInCents > 0 && <div className="tr"><span>Delivery</span><span>{money(o.deliveryFeeInCents)}</span></div>}
              <div className="tr grand"><span>Total</span><span data-total>{money(o.grandTotalInCents)}</span></div>
              <div className="gstline">includes {money(o.gstInCents)} GST</div>
              <div className="tr"><span>Paid</span><span data-paid>{money(o.paidTotalInCents)} · {o.paymentState.replace(/_/g, ' ')}</span></div>
            </div>
          </section>

          {!live && <div className="acct-pad"><Link to={`/r/${rid}/s/${sid}/track/${o.orderId}`} className="linky">See the order's timeline</Link></div>}
        </>
      )}
    </div>
  );
}
